import { StepperOrientation, StepperSelectionEvent } from '@angular/cdk/stepper';
import { NgIf, AsyncPipe } from '@angular/common';
import { AfterViewInit, ChangeDetectionStrategy, ChangeDetectorRef, Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatDivider } from '@angular/material/divider';
import { MatStepper, MatStepperIntl, MatStep, MatStepLabel } from '@angular/material/stepper';
import { AuthorizationSelectors } from '@next/core-lib/authorization';
import {
  CartActions,
  CartLine,
  CartLineSource,
  CartSelectors,
  DeliveryInformation,
  DistributionChainState,
  OrderTypes,
  ProductSupplement,
  Silo,
  SiloDistributionRequirements,
} from '@next/core-lib/cart';
import { AbstractDialog, AbstractDialogData, AbstractDialogResultData, AgrDialogModule } from '@next/core-lib/dialog';
import { LocaleService, AgrI18nSharedModule } from '@next/core-lib/i18n';
import { BreakpointService, BreakpointServiceInjector } from '@next/core-lib/responsive';
import { UntilDestroy } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { Observable, of } from 'rxjs';
import { map, take, tap } from 'rxjs/operators';

import { LineItem, ProductWizardMode, ProductWizardTab, QuantityItem, SkuCatalogItemViewData } from '../../models/product.models';
import * as ProductActions from '../../store/actions';
import * as ProductSelectors from '../../store/selectors';

import { ProductDeliveryInformationComponent } from '../product-delivery-information/product-delivery-information.component';
import { ProductQuantityComponent } from '../product-quantity/product-quantity.component';
import { ProductSiloComponent } from '../product-silo/product-silo.component';
import { ProductSupplementComponent } from '../product-supplement/product-supplement.component';

//Please keep the steps in the order in which you want them to appear on the site
const initialSteps = [
  { tab: ProductWizardTab.QUANTITY, title: 'product.wizard.quantity.tab-title' },
  { tab: ProductWizardTab.SILOS, title: 'product.wizard.silo.tab-title' },
  { tab: ProductWizardTab.PRODUCT_SUPPLEMENTS, title: 'product.wizard.supplement.tab-title' },
  { tab: ProductWizardTab.DELIVERY_INFORMATION, title: 'product.wizard.delivery-moment.tab-title' },
];

let steps = [...initialSteps];

interface ProductWizardDialogData extends AbstractDialogData {
  skuCatalogItem?: SkuCatalogItemViewData;
  cartId?: number;
  cartLine?: CartLine;
  initialSelectedTab?: ProductWizardTab;
  mode: ProductWizardMode;
  orderType?: OrderTypes;
  source?: CartLineSource;
}

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-product-wizard-dialog',
  templateUrl: './product-wizard-dialog.component.html',
  styleUrls: ['./product-wizard-dialog.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    AgrDialogModule,
    NgIf,
    MatDivider,
    MatStepper,
    MatStep,
    MatStepLabel,
    ProductQuantityComponent,
    ProductSiloComponent,
    ProductSupplementComponent,
    ProductDeliveryInformationComponent,
    AsyncPipe,
    AgrI18nSharedModule,
  ],
})
export class ProductWizardDialogComponent extends AbstractDialog<ProductWizardDialogData> implements OnInit, AfterViewInit {
  @ViewChild('wizardStepper') wizardStepper: MatStepper;

  readonly selectedCartLine$ = this.store.select(ProductSelectors.selectSelectedCartLine);

  public cartId: number;
  public cartLine: CartLine;
  public skuId: number;
  public lineItem: LineItem;
  public quantityItem: QuantityItem;
  public isSiloPageVisited$: Observable<boolean> = of(false);
  protected deliveryDatesLoading: boolean;

  isLtMd$ = this.breakpointService.ltMd$
    .pipe(
      tap((isLtMd) => {
        this.orientation = isLtMd ? 'vertical' : 'horizontal';
      }),
    )
    .subscribe();
  isLtSm$ = this.breakpointService.ltSm$;
  orientation: StepperOrientation;

  silosAreOptional = false;
  silosAreRelevant = false;
  silosAreValid = true;
  quantityIsValid = true;
  hasAtLeastAdvisorRole = false;
  isBlockedForReturn = false;

  public deliveryInformationValue: DeliveryInformation | null;
  public deliveryInformationIsValid = true;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: ProductWizardDialogData,
    protected mdDialogRef: MatDialogRef<ProductWizardDialogComponent, AbstractDialogResultData>,
    private readonly store: Store,
    private readonly matStepperIntl: MatStepperIntl,
    private readonly localeService: LocaleService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    @Inject(BreakpointServiceInjector) private readonly breakpointService: BreakpointService,
  ) {
    super();

    this.store
      .select(AuthorizationSelectors.hasAtLeastAdvisorRole)
      .subscribe((hasAtLeastAdvisorRoleValue) => (this.hasAtLeastAdvisorRole = hasAtLeastAdvisorRoleValue));
    this.skuId = data.skuCatalogItem?.skuId;
    this.selectedCartLine$
      .pipe(
        map((cartLine: CartLine) => {
          return cartLine?.deliveryInformation;
        }),
      )
      .subscribe((deliveryInformation) => {
        this.deliveryInformationValue = deliveryInformation;
      });

    this.matStepperIntl.optionalLabel = this.localeService.translate('product.wizard.optional');
    this.matStepperIntl.changes.next();

    const sourceToAdd = this.data.source ? { source: this.data.source } : {};

    this.cartLine = {
      ...data.skuCatalogItem,
      skuTitle: data.skuCatalogItem?.title,
      ...data.cartLine,
      ...sourceToAdd,
    } as CartLine;

    this.quantityItem = {
      quantity: this.cartLine.quantity,
      orderingInformation: this.cartLine.orderingInformation,
      packagingInformation: this.cartLine.packagingInformation,
    };

    this.lineItem = {
      id: this.cartLine.skuId,
      title: this.cartLine.skuTitle,
      quantity: this.cartLine.quantity,
      orderUnit: this.cartLine.orderingInformation.orderUnit,
      productSupplements: this.cartLine.productSupplements,
    };

    this.updateSelectedCartLine();

    if (!data?.cartId) {
      this.store
        .select(CartSelectors.selectCurrentSalesOrderCart)
        .pipe(take(1))
        .subscribe((cart) => {
          this.cartId = cart?.id;
        });
    } else {
      this.cartId = data.cartId;
    }
  }

  ngOnInit(): void {
    if (this.data.mode === ProductWizardMode.ADD) {
      this.selectedCartLine$.pipe(take(1)).subscribe(({ skuId }) => {
        this.skuId = skuId;
        this.store.dispatch(ProductActions.getSkuDeliveryMomentsRequest({ skuId, orderType: this.data.orderType }));
      });
    } else {
      this.store.dispatch(
        ProductActions.getCartLineDeliveryMomentsRequest({
          cartId: this.cartId,
          flexibility: this.cartLine?.deliveryInformation?.flexibility,
        }),
      );
    }

    this.store.dispatch(ProductActions.getSilosRequest());
    this.store.dispatch(ProductActions.getSilosInUseOnDatesRequest());
    this.isBlockedForReturn =
      this.cartLine.orderingInformation.distributionChainState === DistributionChainState.BlockedForReturn ||
      this.cartLine.orderingInformation.distributionChainState === DistributionChainState.TemporaryReturnBlockWebshop;
    this.silosAreOptional = this.cartLine.orderingInformation.siloDistributionRequirements !== SiloDistributionRequirements.mandatory;

    this.silosAreRelevant = this.cartLine.orderingInformation.siloDistributionRequirements !== SiloDistributionRequirements.notRelevant;

    if (this.cartLine.hasProductSupplements) {
      this.store.dispatch(ProductActions.getVariantsRequest());
    }
  }

  ngAfterViewInit(): void {
    this.filterOutOptionalSteps();
    const initialIndex = steps.findIndex((x) => x.tab === this.data.initialSelectedTab);
    if (initialIndex !== -1) {
      this.wizardStepper.selectedIndex = initialIndex;
      this.wizardStepper.steps.get(0).interacted = false;
    } else {
      this.wizardStepper.selectedIndex = 0;
      this.data.disableCancelButton = true;
      this.data.disableAltButton = false;
    }
    this.changeDetectorRef.detectChanges();
  }

  handleQuantityChange(newQuantity: number): void {
    this.cartLine = {
      ...this.cartLine,
      quantity: newQuantity,
    };
    this.updateSelectedCartLine();
  }

  handleSilosChange(newSilos: Silo[]): void {
    this.cartLine = {
      ...this.cartLine,
      silos: newSilos,
    };
    this.updateSelectedCartLine();
  }

  handleDeliveryInformationChange(newDeliveryInformation: DeliveryInformation): void {
    const newCartLine = {
      ...this.cartLine,
      deliveryInformation: newDeliveryInformation,
    };

    // Prevents endless loops between the wizard and the product delivery information component
    if (JSON.stringify(newCartLine) !== JSON.stringify(this.cartLine)) {
      this.cartLine = newCartLine;
      this.updateSelectedCartLine();
    }
  }

  handleProductSupplementsChange(newProductSupplements: ProductSupplement[]): void {
    this.cartLine = {
      ...this.cartLine,
      productSupplements: newProductSupplements,
    };

    this.updateSelectedCartLine();
  }

  updateSelectedCartLine(): void {
    this.resetSteps();
    this.store.dispatch(
      ProductActions.setSelectedCartLine({
        selectedCartLine: this.cartLine,
      }),
    );
  }

  resetSteps() {
    steps = [...initialSteps];
  }

  handleStepperSelectionChange(selectionChange: StepperSelectionEvent): void {
    this.filterOutOptionalSteps();
    this.data.title = steps[selectionChange.selectedIndex].title;
    this.updateDialogFooterButtons(selectionChange.selectedIndex + 1);
  }

  handleQuantityIsValidChange(newQuantityIsValid: boolean): void {
    this.quantityIsValid = newQuantityIsValid;
    this.updateDisableConfirmButton();
  }

  handleDeliveryInformationIsValidChange(newDeliveryInformationIsValid: boolean): void {
    this.deliveryInformationIsValid = newDeliveryInformationIsValid;
    this.updateDisableConfirmButton();
  }

  handleSilosAreValidChange(newSilosAreValid: boolean): void {
    this.silosAreValid = newSilosAreValid;
    this.updateDisableConfirmButton();
  }

  stepBack(): void {
    this.wizardStepper.previous();
  }

  stepForward(): void {
    this.wizardStepper.next();
  }

  addToCart(): void {
    if (!this.quantityIsValid) {
      return;
    }

    if (this.data.mode === ProductWizardMode.ADD) {
      this.store.dispatch(
        CartActions.addCartLineToCart({
          cartLine: this.cartLine,
          cartId: this.cartId,
        }),
      );
    } else {
      this.handleCartLineUpdate();
    }
    this.confirm();
  }

  private filterOutOptionalSteps() {
    if (!this.cartLine.hasProductSupplements) {
      steps = steps.filter((step) => step.tab !== ProductWizardTab.PRODUCT_SUPPLEMENTS);
    }
    if (!this.silosAreRelevant) {
      steps = steps.filter((step) => step.tab !== ProductWizardTab.SILOS);
    }
  }

  private updateDisableConfirmButton(): void {
    this.data = {
      ...this.data,
      disableConfirmButton: !this.quantityIsValid || !this.deliveryInformationIsValid || !this.silosAreValid,
    };
  }

  private handleCartLineUpdate(): void {
    this.store.dispatch(
      CartActions.updateCartLineProductWizard({
        cartId: this.cartId,
        cartLineId: this.cartLine.id,
        quantity: this.cartLine.quantity,
        deliveryInformation: this.cartLine.deliveryInformation,
        siloDistributions: this.cartLine.silos,
        supplements: this.cartLine.hasProductSupplements ? this.cartLine.productSupplements : [],
      }),
    );
  }

  private updateDialogFooterButtons(newSelectedIndex: number): void {
    this.data.disableCancelButton = newSelectedIndex === 1;
    this.data.disableAltButton = newSelectedIndex === this.wizardStepper.steps.length;
  }
}
